import { Inject, Injectable, signal } from '@angular/core';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '@galaxy/crm/static';
import { of, switchMap, tap, catchError, Observable, map, take, combineLatest, withLatestFrom } from 'rxjs';
import { ArchiveFieldSchemaRequestInterface, CRMFieldSchemaApiService, FieldSchemaInterface } from '@vendasta/crm';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable()
export class CustomFieldsManagementTableChangesService {
  private readonly deletedFieldIds = signal<Map<ObjectType, string[]>>(
    new Map([
      ['Contact', []],
      ['Company', []],
    ]),
  );
  private readonly createdFields = signal<Map<ObjectType, FieldSchemaInterface[]>>(
    new Map([
      ['Contact', []],
      ['Company', []],
    ]),
  );
  private readonly updatedFields = signal<Map<ObjectType, FieldSchemaInterface[]>>(
    new Map([
      ['Contact', []],
      ['Company', []],
    ]),
  );
  private deletedFieldIds$ = toObservable(this.deletedFieldIds);
  private createdFields$ = toObservable(this.createdFields);
  private updatedFields$ = toObservable(this.updatedFields);
  changes$ = combineLatest([this.deletedFieldIds$, this.createdFields$, this.updatedFields$]).pipe(
    map(([deletedFieldIds, createdFields, updatedFields]) => ({
      deletedFieldIds,
      createdFields,
      updatedFields,
    })),
  );

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmFieldSchemaApiService: CRMFieldSchemaApiService,
  ) {}

  public createField(objectType: ObjectType, field: FieldSchemaInterface): Observable<boolean> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        return this.crmFieldSchemaApiService.createFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          fieldSchema: {
            ...field,
            namespace,
          },
        });
      }),
      withLatestFrom(this.config.namespace$),
      switchMap(([resp, namespace]) =>
        this.crmFieldSchemaApiService.getMultiFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          fieldId: [resp.fieldId],
        }),
      ),
      catchError(() => {
        return of(null);
      }),
      take(1),
      tap((getResponse) =>
        this.createdFields.update((fieldMap) => {
          const fieldsPerObjectType = fieldMap.get(objectType);
          if (getResponse?.fieldSchemas[0]) {
            fieldMap.set(objectType, [...fieldsPerObjectType, getResponse.fieldSchemas[0]]);
          }
          return new Map(fieldMap);
        }),
      ),
      map((response) => response != null),
    );
  }

  public updateField(objectType: ObjectType, field: FieldSchemaInterface): Observable<boolean> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        return this.crmFieldSchemaApiService.updateFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          fieldSchema: {
            ...field,
            namespace,
          },
          fieldMask: {
            paths: ['fieldName', 'fieldDescription', 'externalId'],
          },
        });
      }),
      catchError(() => {
        return of(null);
      }),
      take(1),
      map((response) => response != null),
      tap(() =>
        this.updatedFields.update((fieldMap) => {
          const fieldsPerObjectType = fieldMap.get(objectType);
          const index = fieldsPerObjectType.findIndex((f) => f.fieldId === field.fieldId);
          if (index !== -1) {
            fieldsPerObjectType[index] = field;
            fieldMap.set(objectType, fieldsPerObjectType);
          } else {
            fieldMap.set(objectType, [...fieldsPerObjectType, field]);
          }
          return new Map(fieldMap);
        }),
      ),
    );
  }

  public deleteField(request: ArchiveFieldSchemaRequestInterface): Observable<boolean> {
    return this.crmFieldSchemaApiService.archiveFieldSchema(request).pipe(
      catchError(() => of(null)),
      map((response) => {
        return response != null;
      }),
      take(1),
      tap(() =>
        this.deletedFieldIds.update((fieldMap) => {
          const fieldsPerObjectType = fieldMap.get(request?.crmObjectType as ObjectType);
          const index = fieldsPerObjectType.findIndex((fieldId) => fieldId === request.fieldId);
          if (index === -1) {
            fieldMap.set(request?.crmObjectType as ObjectType, [...fieldsPerObjectType, request.fieldId]);
          }
          return new Map(fieldMap);
        }),
      ),
    );
  }

  public resetChanges(objectType: ObjectType): void {
    const deletedFieldIds = this.deletedFieldIds();
    deletedFieldIds.set(objectType, []);
    const createdFields = this.createdFields();
    createdFields.set(objectType, []);
    const updatedFields = this.updatedFields();
    updatedFields.set(objectType, []);
    this.deletedFieldIds.set(new Map(deletedFieldIds));
    this.createdFields.set(new Map(createdFields));
    this.updatedFields.set(new Map(updatedFields));
  }
}
